/**
 * Amazon API – direct call from frontend.
 * User provides API Key Name and API Key in the Extract form.
 */

import firebase from "../components/firebase";
import { base } from "../components/firebase";

const JUNGLE_SCOUT_BASE = "https://developer.junglescout.com";

export interface KeywordAttribute {
  name?: string;
  organic_rank?: number | null;
  sponsored_rank?: number | null;
  overall_rank?: number | null;
  ppc_bid_exact?: number | null;
  ppc_bid_broad?: number | null;
  monthly_search_volume_exact?: number | null;
  monthly_search_volume_broad?: number | null;
  ease_of_ranking_score?: number | null;
  [key: string]: unknown;
}

export interface KeywordByAsinRow {
  id: string;
  type: string;
  attributes: KeywordAttribute;
}

export interface KeywordsByAsinResult {
  data?: KeywordByAsinRow[];
  errors?: Array<{ title: string; detail: string; code: string; status: string }>;
}

/**
 * Изчислява необходимите продажби за първа страница
 * @param keyword - Обект с данни за ключовата дума (API attributes)
 * @returns Необходими продажби месечно (или null ако няма search volume)
 */
export function estimateSalesToFirstPage(keyword: KeywordAttribute): number | null {
  const searchVolume = keyword.monthly_search_volume_exact ?? 0;
  const organicRank = keyword.organic_rank ?? 0;
  const ppcBidExact = keyword.ppc_bid_exact ?? 0;
  const ppcBidBroad = keyword.ppc_bid_broad ?? 0;

  if (searchVolume === 0) {
    return null;
  }

  // СТЪПКА 1: Базови продажби – 3% от search volume (30% CTR * 10% Conversion)
  const baseSales = searchVolume * 0.03;

  // СТЪПКА 2: Фактор за конкуренция (от PPC bid)
  const ppcBid = ppcBidExact > 0 ? ppcBidExact : ppcBidBroad;
  let competitionFactor: number;
  if (ppcBid === 0 || ppcBid < 0.5) {
    competitionFactor = 1.0;
  } else if (ppcBid < 1.5) {
    competitionFactor = 1.3;
  } else if (ppcBid < 3.0) {
    competitionFactor = 1.6;
  } else {
    competitionFactor = 2.0;
  }

  // СТЪПКА 3: Фактор за позиция (от Organic rank)
  let rankFactor: number;
  if (organicRank === 0 || organicRank > 50) {
    rankFactor = 0.35;
  } else if (organicRank > 20) {
    rankFactor = 0.28;
  } else if (organicRank > 10) {
    rankFactor = 0.22;
  } else {
    rankFactor = 0.12;
  }

  // СТЪПКА 4: Финално изчисление
  const salesNeeded = baseSales * competitionFactor * rankFactor;
  return Math.round(salesNeeded);
}

/** Normalize API key fields: trim and remove newlines (e.g. when pasting). */
function normalizeCredential(value: string): string {
  return value.replace(/\s+/g, "").trim();
}

/** Call Amazon keywords by ASIN API from the frontend. Endpoint: keywords/keywords_by_asin_query (per Python client). */
export async function extractKeywordsByAsin(
  asin: string,
  marketplace: string,
  apiKeyName: string,
  apiKey: string
): Promise<KeywordsByAsinResult> {
  const keyName = normalizeCredential(apiKeyName);
  const key = normalizeCredential(apiKey);

  const url = `${JUNGLE_SCOUT_BASE}/api/keywords/keywords_by_asin_query?marketplace=${encodeURIComponent(marketplace)}`;
  const body = {
    data: {
      type: "keywords_by_asin_query",
      attributes: {
        asins: [asin],
        include_variants: true,
      },
    },
  };

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/vnd.api+json",
      Accept: "application/vnd.junglescout.v1+json",
      Authorization: `${keyName}:${key}`,
      "X-API-Type": "junglescout",
    },
    body: JSON.stringify(body),
  });

  const json = (await response.json()) as KeywordsByAsinResult;

  if (!response.ok) {
    const message = json?.errors?.[0]?.detail || `Amazon API: ${response.status}`;
    throw new Error(message);
  }

  return json;
}

/** Save extracted keyword data to Firebase Realtime Database for the current user. */
export function saveExtractToFirebase(
  asin: string,
  marketplace: string,
  keywords: KeywordByAsinRow[]
): Promise<unknown> {
  const user = firebase.getCurrentUser();
  if (!user?.uid) return Promise.reject(new Error("Not authenticated"));
  const ref = firebase.db.ref(
    `${base}/${user.uid}/jungleScoutExtracts`
  );
  return ref.push({
    asin,
    marketplace,
    extractedAt: new Date().toISOString(),
    keywords: keywords.map((k) => ({
      id: k.id,
      type: k.type,
      attributes: k.attributes,
    })),
  });
}

/** Load saved extracts for the current user. */
export async function getSavedExtracts(): Promise<
  Array<{ id: string; asin: string; marketplace: string; extractedAt: string; keywords: KeywordByAsinRow[] }>
> {
  const user = firebase.getCurrentUser();
  if (!user?.uid) return [];
  const snapshot = await firebase.db
    .ref(`${base}/${user.uid}/jungleScoutExtracts`)
    .once("value");
  const val = snapshot.val();
  if (!val) return [];
  return Object.entries(val).map(([id, v]) => ({
    id,
    ...(v as { asin: string; marketplace: string; extractedAt: string; keywords: KeywordByAsinRow[] }),
  }));
}

// --- Product Database Query (Amazon API) ---

export interface ProductDatabaseQueryAttributes {
  product_tiers?: string[];
  seller_types?: string[];
  include_keywords?: string[];
  exclude_keywords?: string[];
  categories?: string[];
  exclude_top_brands?: boolean;
  exclude_unavailable_products?: boolean;
  min_price?: number;
  max_price?: number;
  min_net?: number;
  max_net?: number;
  min_rank?: number;
  max_rank?: number;
  min_sales?: number;
  max_sales?: number;
  min_revenue?: number;
  max_revenue?: number;
  min_reviews?: number;
  max_reviews?: number;
  min_rating?: number;
  max_rating?: number;
  min_weight?: number;
  max_weight?: number;
  min_sellers?: number;
  max_sellers?: number;
  min_lqs?: number;
  max_lqs?: number;
  min_updated_at?: string;
  max_updated_at?: string;
}

export interface ProductDatabaseResultRow {
  id: string;
  type: string;
  attributes: {
    title?: string | null;
    price?: number | null;
    reviews?: number | null;
    rating?: number | null;
    image_url?: string | null;
    category?: string | null;
    brand?: string | null;
    product_rank?: number | null;
    seller_type?: string | null;
    approximate_30_day_units_sold?: number | null;
    approximate_30_day_revenue?: number | null;
    fee_breakdown?: { total_fees?: number } | null;
    parent_asin?: string | null;
    [key: string]: unknown;
  };
}

export interface ProductDatabaseQueryResponse {
  data?: ProductDatabaseResultRow[];
  errors?: Array<{ title: string; detail: string; code: string; status: string }>;
  meta?: { total_items?: number };
  links?: { next?: string };
}

/**
 * Call Amazon Product Database Query API.
 * POST /api/product_database_query?marketplace=us
 */
export async function productDatabaseQuery(
  apiKeyName: string,
  apiKey: string,
  marketplace: string,
  attributes: ProductDatabaseQueryAttributes,
  pageSize = 100
): Promise<ProductDatabaseQueryResponse> {
  const keyName = normalizeCredential(apiKeyName);
  const key = normalizeCredential(apiKey);
  const mkt = marketplace.toLowerCase() === "usa" ? "us" : marketplace.toLowerCase();

  const url = `${JUNGLE_SCOUT_BASE}/api/product_database_query?marketplace=${encodeURIComponent(mkt)}&page[size]=${pageSize}`;
  const body = {
    data: {
      type: "product_database_query",
      attributes: {
        ...(attributes.product_tiers?.length ? { product_tiers: attributes.product_tiers } : {}),
        ...(attributes.seller_types?.length ? { seller_types: attributes.seller_types } : {}),
        ...(attributes.include_keywords?.length ? { include_keywords: attributes.include_keywords } : {}),
        ...(attributes.exclude_keywords?.length ? { exclude_keywords: attributes.exclude_keywords } : {}),
        ...(attributes.categories?.length ? { categories: attributes.categories } : {}),
        ...(attributes.exclude_top_brands != null ? { exclude_top_brands: attributes.exclude_top_brands } : {}),
        ...(attributes.exclude_unavailable_products != null
          ? { exclude_unavailable_products: attributes.exclude_unavailable_products }
          : { exclude_unavailable_products: true }),
        ...(attributes.min_price != null ? { min_price: attributes.min_price } : {}),
        ...(attributes.max_price != null ? { max_price: attributes.max_price } : {}),
        ...(attributes.min_net != null ? { min_net: attributes.min_net } : {}),
        ...(attributes.max_net != null ? { max_net: attributes.max_net } : {}),
        ...(attributes.min_rank != null ? { min_rank: attributes.min_rank } : {}),
        ...(attributes.max_rank != null ? { max_rank: attributes.max_rank } : {}),
        ...(attributes.min_sales != null ? { min_sales: attributes.min_sales } : {}),
        ...(attributes.max_sales != null ? { max_sales: attributes.max_sales } : {}),
        ...(attributes.min_revenue != null ? { min_revenue: attributes.min_revenue } : {}),
        ...(attributes.max_revenue != null ? { max_revenue: attributes.max_revenue } : {}),
        ...(attributes.min_reviews != null ? { min_reviews: attributes.min_reviews } : {}),
        ...(attributes.max_reviews != null ? { max_reviews: attributes.max_reviews } : {}),
        ...(attributes.min_rating != null ? { min_rating: attributes.min_rating } : {}),
        ...(attributes.max_rating != null ? { max_rating: attributes.max_rating } : {}),
        ...(attributes.min_weight != null ? { min_weight: attributes.min_weight } : {}),
        ...(attributes.max_weight != null ? { max_weight: attributes.max_weight } : {}),
        ...(attributes.min_sellers != null ? { min_sellers: attributes.min_sellers } : {}),
        ...(attributes.max_sellers != null ? { max_sellers: attributes.max_sellers } : {}),
        ...(attributes.min_lqs != null ? { min_lqs: attributes.min_lqs } : {}),
        ...(attributes.max_lqs != null ? { max_lqs: attributes.max_lqs } : {}),
        ...(attributes.min_updated_at ? { min_updated_at: attributes.min_updated_at } : {}),
        ...(attributes.max_updated_at ? { max_updated_at: attributes.max_updated_at } : {}),
      },
    },
  };

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/vnd.api+json",
      Accept: "application/vnd.junglescout.v1+json",
      Authorization: `${keyName}:${key}`,
      "X-API-Type": "junglescout",
    },
    body: JSON.stringify(body),
  });

  const json = (await response.json()) as ProductDatabaseQueryResponse;

  if (!response.ok) {
    const message = json?.errors?.[0]?.detail || `Amazon API: ${response.status}`;
    throw new Error(message);
  }

  return json;
}

/**
 * Map Amazon product_database_result to ProductDataRow (our app model).
 */
export function mapProductDatabaseResultToRow(
  item: ProductDatabaseResultRow,
  marketplace: string
): import("../components/ProductDataService/types").ProductDataRow {
  const a = item.attributes;
  const idParts = (item.id || "").split("/");
  const asin = (a.parent_asin || idParts[idParts.length - 1] || "").toString();

  return {
    asin,
    title: a.title ?? "",
    imageUrl: a.image_url ?? "",
    salePrice: a.price ?? 0,
    amazonFees: a.fee_breakdown?.total_fees ?? 0,
    reviewCount: a.reviews ?? 0,
    averageRating: a.rating ?? 0,
    category: a.category ?? "",
    categoryRank: a.product_rank ?? 0,
    sellerType: a.seller_type ?? "",
    brand: a.brand ?? "",
    sales30Days: a.approximate_30_day_units_sold ?? 0,
    revenue30Days: a.approximate_30_day_revenue ?? 0,
  };
}
